<script setup lang="ts">
import DataTable from '@/components/DataTable/DataTable.vue';
import { isPlainClick } from '@/components/DataTable/rowClick';
import { TRACK_MATCHING_CUSTOM_COLUMNS as additionalColumns } from '@/composables/searchTableCustomColumns';
import { usePersistentTable } from '@/composables/usePersistentTable';
import { useSavedViews } from '@/composables/useSavedViews';
import { computed, ref, watch } from 'vue';
import { useTrackMatchingApi } from '@/composables/api/TrackMatchingApi';
import TrackMatchingTableHeader from '@/components/DataTable/TrackMatchingTableHeader.vue';
import TrackMatchingColumnActions from '@/components/DataTable/TrackMatchingColumnActions.vue';
import TrackMatchingHistoryButton from '@/components/DataTable/TrackMatchingHistoryButton.vue';
import TrackMatchingColumnMapping from '@/components/TrackMatchingColumnMapping.vue';
import TrackMatchingArtistCell from '@/components/DataTable/TrackMatchingArtistCell.vue';
import PlayTrackButton from '@/components/PlayTrackButton.vue';
import type { TableColumn } from '@/types/tableTypes';
import { useCursorPager, type PagerDirection } from '@/composables/useCursorPager';
import { useInspectorTopOffset } from '@/composables/useInspectorTopOffset';
import { useMutation } from '@tanstack/vue-query';
import { isAxiosError } from 'axios';
import {
  postLiveTrackActionBulkCreate,
  type LiveTrackActionBulkCreateItem,
} from '@/composables/api/useCrudApi';
import {
  isLiveTrackMatchDataProcessing,
  pendingSaveBlockedByProcessingLiveTrack,
} from '@/trackMatching/trackMatchingProcessingGuard';
import { liveTrackBulkSaveAllowsLeave } from '@/trackMatching/trackMatchingBulkSave';
import { readNestedDisplay, readArtistsArray } from '@/trackMatching/artistRenameSource';
import { readNestedId } from '@/utils/nestedRelation';
import { toast } from 'vue-sonner'
import { onMounted, onUnmounted } from 'vue';
import { onBeforeRouteLeave, onBeforeRouteUpdate, useRoute } from 'vue-router';
import { useRoutedSavedView } from '@/composables/useRoutedSavedView';
import { parseViewParam } from '@/composables/routedSavedView';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import TrackMatchingPusherMonitor from '@/components/TrackMatchingPusherMonitor.vue';
import {
  useTrackMatchingStore,
  SOURCE_TYPES,
  RAW_DDEX_EXPAND_CONTROLS_ROW_SOURCE,
} from '@/stores/trackMatchingStore';
import {
  getEffectiveTrackMatchStatusId,
  trackMatchStatusRelationFromId,
} from '@/constants/trackMatchStatus';
import type { TrackMatchingAction } from '@/trackMatching/trackMatchingTypes';
import type { IScope } from '@/types/filterBuilderTypes';
import { useRawDdexParties } from '@/composables/api/useRawDdexParties';
import { useAuthStore } from '@/stores/authStore';
import { useHistoryInspector } from '@/composables/useHistoryInspector';
import { useOpenRecordInInspector } from '@/composables/useOpenRecordInInspector';
import { useInspectorStore } from '@/stores/inspectorStore';
import { trackMatchRecordFor } from '@/trackMatching/trackMatchRecordFor';
import { parseRawDdexRowId, rawDdexRowId } from '@/trackMatching/rawDdexRowId';
import type { SavedViewRecord } from '@/utils/savedViews';

const authStore = useAuthStore();
const { openUserHistory } = useHistoryInspector();
const inspectorStore = useInspectorStore();
const { canOpen, openRecord } = useOpenRecordInInspector();

/** A row can be opened when it stands for a record that the side peek knows how to show. */
function isTrackMatchRowOpenable(row: Record<string, unknown>): boolean {
  const record = trackMatchRecordFor(row);
  return record !== null && canOpen(record.entityId);
}

/**
 * Opens the clicked row's record in the side peek.
 * A click with cmd, ctrl, shift or alt held opens nothing, as on the other pages.
 */
function onTrackMatchRowClick(row: Record<string, unknown>, event: MouseEvent): void {
  if (!isPlainClick(event)) return;
  const record = trackMatchRecordFor(row);
  if (!record || !canOpen(record.entityId)) return;
  openRecord(record.entityId, record.recordId);
}

/**
 * The row id of the record the side peek is showing, so that row is highlighted.
 * A Live Track row's id is the Live Track's id.
 * A Raw DDEX row's id is `d_` followed by the Raw DDEX track's id.
 * Any other record in the peek highlights no row.
 */
const openTrackMatchRowId = computed((): string | number | null => {
  if (!inspectorStore.isOpen) return null;
  const { entityId, recordId } = inspectorStore.componentProps as {
    entityId?: unknown;
    recordId?: unknown;
  };
  if (typeof recordId !== 'number' && typeof recordId !== 'string') return null;
  if (entityId === 'LiveTrack') return recordId;
  if (entityId === 'RawDdexTrack') return rawDdexRowId(recordId);
  return null;
});

// Keep the side-peek inspector below the toolbar row so the paginator,
// filters, and save controls stay visible and clickable while it is open.
const toolbarRef = ref<HTMLElement | null>(null);
useInspectorTopOffset(toolbarRef);

/** Raw DDEX list is capped before “show more”; Spotify sits below Raw DDEX and above the expand row. */
const RAW_DDEX_MATCH_PREVIEW_COUNT = 5;

/** Uppercase live_track_codes with a track-matching async op in flight (one TrackMatchingPusherMonitor per code). */
const activeMatchingCodes = ref(new Set<string>());

function removeMatchingMonitor(codeUpper: string): void {
  if (!activeMatchingCodes.value.has(codeUpper)) return;
  const next = new Set(activeMatchingCodes.value);
  next.delete(codeUpper);
  activeMatchingCodes.value = next;
}

const rawDdexMatchesExpandedLiveTrackIds = ref(new Set<string | number>());
/** After first “Show more”, all Raw DDEX rows for that live track stay mounted; collapse only toggles `hidden`. */
const rawDdexHydratedLiveTrackIds = ref(new Set<string | number>());

function expandRawDdexMatchesForLiveTrack(liveTrackId: string | number) {
  const nextExpanded = new Set(rawDdexMatchesExpandedLiveTrackIds.value);
  nextExpanded.add(liveTrackId);
  rawDdexMatchesExpandedLiveTrackIds.value = nextExpanded;
  const nextHydrated = new Set(rawDdexHydratedLiveTrackIds.value);
  nextHydrated.add(liveTrackId);
  rawDdexHydratedLiveTrackIds.value = nextHydrated;
}

function collapseRawDdexMatchesForLiveTrack(liveTrackId: string | number) {
  const next = new Set(rawDdexMatchesExpandedLiveTrackIds.value);
  next.delete(liveTrackId);
  rawDdexMatchesExpandedLiveTrackIds.value = next;
}

function clearRawDdexExpandUiState() {
  rawDdexMatchesExpandedLiveTrackIds.value = new Set();
  rawDdexHydratedLiveTrackIds.value = new Set();
}

/** Turn backend/minio/S3-style errors into a short message for toasts */
function getFriendlyErrorMessage(error: unknown): string {
  const raw =
    (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ??
    (error as { message?: string })?.message ??
    '';
  const lower = raw.toLowerCase();
  if (lower.includes('nosuchkey') || (lower.includes('404') && lower.includes('not found'))) {
    return 'Audio file not found.';
  }
  if (lower.includes('getobject') || lower.includes('minio') || lower.includes('s3') || lower.includes('the specified key does not exist')) {
    return 'Storage error: file not found or unavailable.';
  }
  if (raw.length > 120) {
    return raw.slice(0, 117) + '...';
  }
  return raw || 'Unknown error occurred';
}

const { 
  pinnedColumns, 
  unpinnedColumns, 
  columnSort, 
  compositeFilter, 
  pageIndex, 
  pageSize,
  setColumnSize,
  toggleSort,
  updateColumnState,
  setFilter,
} = usePersistentTable({additionalColumns});

// This page's saved views live in the section sidebar. Opening one goes through
// the address (`?view=<id>`). The page only interposes to run its unsaved-changes
// guard before a view replaces pending matches.
const { activeView: activeSavedView, handlers: savedViewHandlers } = useSavedViews();

// Initialize the track matching store
const trackMatchingStore = useTrackMatchingStore();

// Party list + resolver for showing party names in table cells (by numeric id)
const { selectPartyDisplayNamesById } = useRawDdexParties(true);

// Get the current route for watching route changes
const route = useRoute();

// Add confirmation modal state
const showConfirmModal = ref(false);
const pendingNavigation = ref<(() => void) | null>(null);
// Set when the pending action is a route change that must be refused on Cancel,
// so the address goes back to the view that is really on screen.
const pendingNavigationCancel = ref<(() => void) | null>(null);

// Cursor (search_after) paging — any page (incl. Last) without tripping the 10k window (TABASCO-1HT).
const { cursor, pageNumber, cachedTotal, hasNext, hasPrev, reset: resetPager, captureMeta, navigate: navigateCursor } =
  useCursorPager(pageSize);

// Cursor tokens are only valid for a fixed sort + filter + page size. Register this reset BEFORE
// useTrackMatchingApi so a change nulls the cursor before vue-query's options watcher (registered
// inside the composable) reacts — otherwise the first refetch goes out with the previous page's
// token under the new filter (a stale, out-of-window request, plus a wasted double fetch).
watch(
  () => JSON.stringify([columnSort.value, compositeFilter.value, pageSize.value]),
  () => resetPager(),
);

const {
  data,
  isLoading,
  dataUpdatedAt,
  refetch: refetchTrackMatching,
} = useTrackMatchingApi({ cursor, pageSize, sort: columnSort, filter: compositeFilter });

// Add beforeunload event listener
const handleBeforeUnload = (event: BeforeUnloadEvent) => {
  if (queuedLiveTrackCount.value > 0) {
    event.preventDefault();
    event.returnValue = '';
    return '';
  }
};

// Vue Router navigation guard
onBeforeRouteLeave((to, from, next) => {
  if (queuedLiveTrackCount.value > 0) {
    showConfirmModal.value = true;
    pendingNavigation.value = () => next();
    // Don't call next() here - let the modal handle it
  } else {
    next();
  }
});

// Opening another saved view from the sidebar changes only `?view=` in the
// address, so the page stays mounted and the leave guard above never runs.
// This asks the same question before the view replaces pending matches. It
// does not ask when the address is only catching up with the view already
// open, for example right after a new view is saved.
onBeforeRouteUpdate((to, from, next) => {
  const requestedViewId = parseViewParam(to.query.view);
  const opensAnotherView =
    requestedViewId !== null &&
    requestedViewId !== parseViewParam(from.query.view) &&
    requestedViewId !== (activeSavedView.value?.id ?? null);
  if (opensAnotherView && queuedLiveTrackCount.value > 0) {
    showConfirmModal.value = true;
    pendingNavigation.value = () => next();
    pendingNavigationCancel.value = () => next(false);
  } else {
    next();
  }
});

// Discard navigation
const discardNavigation = () => {
  showConfirmModal.value = false;
  if (pendingNavigation.value) {
    // Clear the track matching store state before executing the navigation action
    trackMatchingStore.clearStates();
    clearRawDdexExpandUiState();
    pendingNavigation.value();
    pendingNavigation.value = null;
  }
  pendingNavigationCancel.value = null;
};

// Save navigation
const saveNavigation = () => {
  processRowsHandler()
    .then(({ allowLeave }) => {
      if (allowLeave) {
        discardNavigation();
      } else {
        cancelNavigation();
      }
    })
    .catch(() => {
      cancelNavigation();
    });
};

// Cancel navigation
const cancelNavigation = () => {
  showConfirmModal.value = false;
  if (pendingNavigation.value) {
    // If this was triggered by Vue Router, we need to call next(false) to prevent navigation
    pendingNavigation.value = null;
  }
  if (pendingNavigationCancel.value) {
    pendingNavigationCancel.value();
    pendingNavigationCancel.value = null;
  }
  // Note: We don't clear the store here because the user wants to keep their changes
};

// Set up and clean up event listeners
onMounted(() => {
  window.addEventListener('beforeunload', handleBeforeUnload);
});

onUnmounted(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload);
  // Clear the track matching store when component is unmounted
  trackMatchingStore.clearStates();
  clearRawDdexExpandUiState();
});

const columnsResizeHandler = (columnId: string, width: number) => {
  setColumnSize(columnId, width);
};

const columnsSortHandler = (columnField: string) => {
  toggleSort(columnField);
};

const columnsReorderHandler = (columns: TableColumn[]) => {
  updateColumnState(columns);
};

interface TrackData {
  live_track: { id: string | number; [key: string]: unknown };
  matches?: {
    raw_ddex?: Array<{ id: string | number; [key: string]: unknown }>;
    spotify?: { id: string | number; [key: string]: unknown };
  };
}

interface ProcessedTrackData {
  id: string | number;
  source: string;
  live_track_id: string | number;
  [key: string]: unknown;
}

// Type for row data in the template
type TableRowData = Record<string, unknown> & { 
  live_track_id: string | number; 
  source: string; 
  id: string | number; 
  isrc?: string; 
  player?: string;
  track_match_types?: Array<string | number>;
  match_type?: string;
  match_sender_party_ids?: number[];
  match_sent_on_behalf_of_party_ids?: number[];
  raw_ddex_party_sender_id?: number | null;
  raw_ddex_party_sent_on_behalf_of_id?: number | null;
  rawDdexExpandKind?: 'more' | 'less';
  rawDdexHiddenCount?: number;
  /** When true, DataTable hides the `<tr>` (Raw DDEX rows 6+ after hydration). */
  matchTableRowHidden?: boolean;
};

/**
 * Per–live-track rows derived from API + store. Does not depend on Raw DDEX expand/collapse,
 * so toggling show more / less only recomputes `processedData` (cheap flatten), not merges/state per track.
 */
interface TrackMatchBundle {
  liveTrackId: string | number;
  liveRow: ProcessedTrackData;
  /** Shown after Raw DDEX candidates, immediately before show more / show less when applicable */
  spotifyRow: ProcessedTrackData | null;
  rawDdexRows: ProcessedTrackData[];
  rawDdexTruncatable: boolean;
}

/** API-only bundles; queue visibility is applied in {@see processedData} so link/ISRC toggles stay cheap. */
const trackMatchBundles = computed((): TrackMatchBundle[] => {
  const tracks = data.value?.data;
  if (!tracks?.length) {
    return [];
  }
  return tracks.map((track: TrackData) => {
    const liveTrackId = track.live_track.id;
    const mergedLiveTrack = track.live_track as ProcessedTrackData;
    const liveRow: ProcessedTrackData = {
      ...mergedLiveTrack,
      // ES-hydrated live tracks carry `track_match_status_id` but not the nested
      // {id, display_string} the foreign-model "Track Match Status" column renders, so the
      // cell shows blank. Derive it from the id with the same helpers the store uses for
      // optimistic patches; keep any nested relation the API already supplied.
      track_match_status:
        trackMatchStatusRelationFromId(getEffectiveTrackMatchStatusId(mergedLiveTrack)) ??
        mergedLiveTrack.track_match_status,
      source: SOURCE_TYPES.LIVE_TRACK,
      live_track_id: liveTrackId,
    };

    const rawDdexRows: ProcessedTrackData[] = [];

    const spotifyRow: ProcessedTrackData | null = track.matches?.spotify
      ? {
          ...track.matches.spotify,
          id: `sp_${track.matches.spotify.id}`,
          source: SOURCE_TYPES.SPOTIFY,
          live_track_id: liveTrackId,
        }
      : null;
    const rawDdexList = track.matches?.raw_ddex;
    if (rawDdexList?.length) {
      rawDdexRows.push(
        ...rawDdexList.map((item) => ({
          ...item,
          id: rawDdexRowId(item.id),
          source: SOURCE_TYPES.RAW_DDEX,
          live_track_id: liveTrackId,
        }))
      );
    }

    return {
      liveTrackId,
      liveRow,
      spotifyRow,
      rawDdexRows,
      rawDdexTruncatable: rawDdexRows.length > RAW_DDEX_MATCH_PREVIEW_COUNT,
    };
  });
});

function isMatchTableRowHiddenForDataTable(row: Record<string, unknown>): boolean {
  return (row as TableRowData).matchTableRowHidden === true;
}

/**
 * Map a track-matching row to <PlayTrackButton> props. Row source discriminates the kind;
 * raw-ddex / spotify ids are recovered from the prefixed row id (`d_<id>` / `sp_<id>`) set
 * when the bundle was built.
 */
function playButtonPropsForRow(row: TableRowData) {
  const title = (row.name as string) ?? (row.title as string) ?? null;
  const coverUrl =
    (row.cover_art_signed_url as string) ?? (row.cover_art_url as string) ?? null;
  const rowId = String(row.id ?? '');
  // Live-track rows expose nested {id, display_string} refs (FieldMappings::nestedModelField).
  // Raw-ddex rows are normalized by LiveTrackMatchDTO to the same nested shape, so reading
  // .display_string works for both bundle row kinds. The album_name / artists fallbacks
  // cover rows that come straight off the raw RawDdexTrack search payload (no DTO wrap).
  const artist = readNestedDisplay(row.artist)
    ?? readArtistsArray(row.artists)
    ?? null;
  const album = readNestedDisplay(row.album)
    ?? ((row.album_name as string | null | undefined) ?? null);

  if (row.source === SOURCE_TYPES.LIVE_TRACK) {
    return {
      kind: 'live_track' as const,
      liveTrackCode: (row.code as string) ?? null,
      title,
      artist,
      album,
      coverUrl,
      // Records for the player bar's identity lines. Every bundle row carries
      // its live track's id; ids for artist / album exist only on the nested
      // {id, display_string} shape, so a row that fell back to the DDEX
      // `artists` array or a plain `album_name` leaves those lines plain text.
      liveTrackId: (row.live_track_id as number | null) ?? null,
      artistId: readNestedId(row.artist),
      albumId: readNestedId(row.album),
    };
  }
  if (row.source === SOURCE_TYPES.RAW_DDEX) {
    return {
      kind: 'raw_ddex' as const,
      rawDdexTrackId: parseRawDdexRowId(rowId),
      title,
      artist,
      album,
      coverUrl,
    };
  }
  return {
    kind: 'spotify' as const,
    spotifyTrackId: rowId.replace(/^sp_/, '') || null,
    title,
    artist,
    album,
    coverUrl,
  };
}

const processedData = computed(() => {
  const expandedSet = rawDdexMatchesExpandedLiveTrackIds.value;
  const hydratedSet = rawDdexHydratedLiveTrackIds.value;
  const bundles = trackMatchBundles.value;
  const queueVisibilitySnapshot = trackMatchingStore.queueVisibilityByLiveTrackId.value;
  const acc: ProcessedTrackData[] = [];
  const previewCap = RAW_DDEX_MATCH_PREVIEW_COUNT;

  for (const bundle of bundles) {
    acc.push(bundle.liveRow);
    const queueVisibility =
      queueVisibilitySnapshot[bundle.liveTrackId] ??
      trackMatchingStore.getQueueVisibilityForLiveTrack(bundle.liveTrackId);
    const effectiveRawDdexTrackId = bundle.liveRow.raw_ddex_track_id;
    const showMatchSection =
      queueVisibility.unlinkQueued ||
      !effectiveRawDdexTrackId ||
      queueVisibility.hasQueuedLink;
    if (!showMatchSection || queueVisibility.hideCandidateRows) {
      continue;
    }
    const raw = bundle.rawDdexRows;
    if (!raw.length) {
      if (bundle.spotifyRow) {
        acc.push(bundle.spotifyRow);
      }
      continue;
    }
    if (!bundle.rawDdexTruncatable) {
      acc.push(...raw);
      if (bundle.spotifyRow) {
        acc.push(bundle.spotifyRow);
      }
      continue;
    }
    const liveId = bundle.liveTrackId;
    const isHydrated = hydratedSet.has(liveId);
    const isExpanded = expandedSet.has(liveId);

    if (!isHydrated) {
      if (isExpanded) {
        acc.push(...raw);
        if (bundle.spotifyRow) {
          acc.push(bundle.spotifyRow);
        }
        acc.push({
          id: `raw_ddex_collapse_${liveId}`,
          live_track_id: liveId,
          source: RAW_DDEX_EXPAND_CONTROLS_ROW_SOURCE,
          rawDdexExpandKind: 'less' as const,
        });
      } else {
        acc.push(...raw.slice(0, previewCap));
        if (bundle.spotifyRow) {
          acc.push(bundle.spotifyRow);
        }
        acc.push({
          id: `raw_ddex_expand_${liveId}`,
          live_track_id: liveId,
          source: RAW_DDEX_EXPAND_CONTROLS_ROW_SOURCE,
          rawDdexExpandKind: 'more' as const,
          rawDdexHiddenCount: raw.length - previewCap,
        });
      }
      continue;
    }

    for (let rawIndex = 0; rawIndex < raw.length; rawIndex++) {
      acc.push({
        ...raw[rawIndex],
        matchTableRowHidden: rawIndex >= previewCap && !isExpanded,
      });
    }
    if (bundle.spotifyRow) {
      acc.push(bundle.spotifyRow);
    }
    if (!isExpanded && raw.length > previewCap) {
      acc.push({
        id: `raw_ddex_expand_${liveId}`,
        live_track_id: liveId,
        source: RAW_DDEX_EXPAND_CONTROLS_ROW_SOURCE,
        rawDdexExpandKind: 'more' as const,
        rawDdexHiddenCount: raw.length - previewCap,
      });
    } else if (isExpanded && raw.length > previewCap) {
      acc.push({
        id: `raw_ddex_collapse_${liveId}`,
        live_track_id: liveId,
        source: RAW_DDEX_EXPAND_CONTROLS_ROW_SOURCE,
        rawDdexExpandKind: 'less' as const,
      });
    }
  }
  return acc;
});

// Resync when a fetch completes (`dataUpdatedAt`), not only when the `data` ref identity changes.
watch(dataUpdatedAt, () => {
  const newData = data.value;

  // Capture this page's adjacent-page tokens (and the total, when the response carried one) so the
  // pager can move to the next/previous pages and render an accurate "X / Y".
  captureMeta(newData?.meta);

  if (newData?.data) {
    trackMatchingStore.clearOptimisticPatches();
    // Store all row data in the store immediately when API data loads
    newData.data.forEach((track: TrackData) => {
      // Store live track data
      trackMatchingStore.setMatchData(
        track.live_track.id, 
        SOURCE_TYPES.LIVE_TRACK, 
        track.live_track.id, 
        track.live_track
      );
      
      // Store matched rows data (always store them, regardless of visibility)
      if (track.matches?.raw_ddex) {
        track.matches.raw_ddex.forEach((item) => {
          trackMatchingStore.setMatchData(
            track.live_track.id,
            SOURCE_TYPES.RAW_DDEX,
            rawDdexRowId(item.id),
            item
          );
        });
      }
      
      if (track.matches?.spotify) {
        trackMatchingStore.setMatchData(
          track.live_track.id,
          SOURCE_TYPES.SPOTIFY,
          `sp_${track.matches.spotify.id}`,
          track.matches.spotify
        );
      }
    });
    for (const track of newData.data) {
      trackMatchingStore.syncQueueVisibilityForLiveTrack(track.live_track.id);
    }
  }
}, { immediate: true });

// Watch for route changes and clear the store when navigating away
watch(() => route.path, () => {
  // Clear the track matching store when the route changes
  trackMatchingStore.clearStates();
  clearRawDdexExpandUiState();
});

const queuedLiveTrackCount = computed(() => trackMatchingStore.countQueuedLiveTracks());

// Unique live tracks with pending actions (used for processing guard on save).
// Derive from the store's canonical queued-ids logic rather than re-listing the pending-action
// flags here — a local copy previously drifted and omitted pendingArtistRename, letting an
// artist-rename-only track slip past the "save while processing" guard. Touching states.value keeps
// this computed reactive to queue changes.
const liveTrackIdsWithPendingActions = computed(() => {
  void trackMatchingStore.states.value;
  return trackMatchingStore.queuedLiveTrackIds();
});

function runWithUnsavedChangesGuard(action: () => void): void {
  if (queuedLiveTrackCount.value > 0) {
    showConfirmModal.value = true;
    pendingNavigation.value = action;
  } else {
    action();
  }
}

// SAU-184 artist clean-up: confirm before queueing a rename; clicking an already-queued source
// row just toggles it off (no dialog), mirroring the snappy ISRC/Link toggle.
type ArtistRenameTarget = {
  liveTrackId: string | number;
  sourceId: string | number;
  source: string;
  name: string;
};
const showArtistRenameModal = ref(false);
const artistRenameTarget = ref<ArtistRenameTarget | null>(null);

// Clear the target whenever the dialog closes by any route — the Cancel button calls
// cancelArtistRename(), but X / Escape / backdrop close via v-model:open without emitting @cancel,
// which would otherwise leave artistRenameTarget populated.
watch(showArtistRenameModal, (open) => {
  if (!open) {
    artistRenameTarget.value = null;
  }
});

const artistRenameMessage = computed(() => {
  const target = artistRenameTarget.value;
  if (!target) {
    return '';
  }
  const sourceLabel = target.source === SOURCE_TYPES.SPOTIFY ? 'Spotify' : 'Raw DDEX';
  return `Update this live track's artist from ${sourceLabel} to "${target.name}"? This renames the artist for every track that shares it.`;
});

function onArtistRenameRequest(target: ArtistRenameTarget): void {
  if (trackMatchingStore.isArtistRenameQueuedForSource(target.liveTrackId, target.sourceId)) {
    trackMatchingStore.toggleArtistRename(target.liveTrackId, target.sourceId, target.name);
    return;
  }
  artistRenameTarget.value = target;
  showArtistRenameModal.value = true;
}

function confirmArtistRename(): void {
  const target = artistRenameTarget.value;
  if (target) {
    trackMatchingStore.toggleArtistRename(target.liveTrackId, target.sourceId, target.name);
  }
  showArtistRenameModal.value = false;
  artistRenameTarget.value = null;
}

function cancelArtistRename(): void {
  showArtistRenameModal.value = false;
  artistRenameTarget.value = null;
}

function isLiveTrackProcessing(liveTrackId: string | number): boolean {
  const liveTrackState = trackMatchingStore.getState(
    liveTrackId,
    SOURCE_TYPES.LIVE_TRACK,
    liveTrackId
  );
  if (isLiveTrackMatchDataProcessing(liveTrackState.matchData as Record<string, unknown> | undefined)) {
    return true;
  }

  const apiTrack = data.value?.data?.find(
    (track: TrackData) => track.live_track.id === liveTrackId
  );
  if (apiTrack) {
    return isLiveTrackMatchDataProcessing(apiTrack.live_track as Record<string, unknown>);
  }

  return false;
}

const saveBlockedByProcessing = computed(() =>
  pendingSaveBlockedByProcessingLiveTrack(
    liveTrackIdsWithPendingActions.value,
    isLiveTrackProcessing
  )
);

// Generate actions payload for API
function generateActionsPayload() {
  return trackMatchingStore.generateActionsPayload();
}

function trackMatchingActionToBulkItem(action: TrackMatchingAction): LiveTrackActionBulkCreateItem {
  return {
    live_track_id: action.live_track_id,
    live_track_code: action.live_track_code,
    source: action.source,
    update_fields: action.update_fields,
    update_audio_file: action.update_audio_file,
    update_cover_art: action.update_cover_art,
    raw_ddex_track_id: action.raw_ddex_track_id ?? null,
    live_track_backup: action.live_track_backup,
    rollback_to_action_id: null,
  };
}

const { mutateAsync: submitLiveTrackActionsBulk, isPending: isCreatePending } = useMutation({
  mutationFn: async (actions: TrackMatchingAction[]) => {
    const errorSummaries: string[] = [];
    let createdGroupCount = 0;
    const items = actions.map(trackMatchingActionToBulkItem);

    try {
      const data = await postLiveTrackActionBulkCreate(items);
      createdGroupCount = data.created.length;

      const succeededLiveTrackIds = new Set<string | number>();
      const succeededLiveTrackCodesUpper = new Set<string>();

      for (const created of data.created) {
        for (const localIdx of created.squashed_from_indices) {
          const action = actions[localIdx];
          if (!action) continue;
          succeededLiveTrackIds.add(action.live_track_id);
          const codeUpper = action.live_track_code?.trim().toUpperCase();
          if (codeUpper) {
            succeededLiveTrackCodesUpper.add(codeUpper);
          }
        }
      }

      if (succeededLiveTrackIds.size > 0) {
        trackMatchingStore.clearActionStatesForLiveTracks(succeededLiveTrackIds);
      }

      if (succeededLiveTrackCodesUpper.size > 0) {
        const nextActive = new Set(activeMatchingCodes.value);
        for (const code of succeededLiveTrackCodesUpper) {
          nextActive.add(code);
        }
        activeMatchingCodes.value = nextActive;
      }

      for (const err of data.errors) {
        const trackIds = [
          ...new Set(
            err.indices
              .map((i) => actions[i]?.live_track_id)
              .filter((id): id is string | number => id !== undefined && id !== null),
          ),
        ];
        const suffix = trackIds.length ? ` — live track id(s): ${trackIds.join(', ')}` : '';
        errorSummaries.push(`${err.message}${suffix}`);
      }
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        const status = error.response?.status;
        if (status === 403) {
          errorSummaries.push('You do not have permission to create live track actions.');
        } else if (status === 422) {
          const base = getFriendlyErrorMessage(error);
          const ids = actions.map((a) => a.live_track_id).join(', ');
          errorSummaries.push(`${base} — live track id(s): ${ids}`);
        } else {
          throw error;
        }
      } else {
        throw error;
      }
    }

    if (createdGroupCount > 0) {
      const refreshResult = await refetchTrackMatching();
      if (refreshResult.isError) {
        toast.error(
          `Saved, but refreshing the list failed: ${getFriendlyErrorMessage(refreshResult.error)}`,
          { duration: 6000 },
        );
      }
      const successMessage =
        createdGroupCount === 1
          ? '1 live track action queued'
          : `${createdGroupCount} live track actions queued`;
      const currentUserId = authStore.userData?.id ?? null;
      toast.success(successMessage, {
        duration: 5000,
        // Clicking through opens the History Inspector seeded to this user's
        // recent actions — including the ones just queued (SAU-138).
        ...(currentUserId != null
          ? {
              action: {
                label: 'View history',
                onClick: () => openUserHistory(currentUserId),
              },
            }
          : {}),
      });
    }

    for (const msg of errorSummaries) {
      toast.error(msg, { duration: 5000 });
    }

    return { createdGroupCount, errorSummaries };
  },
  onError: (error: unknown) => {
    if (isAxiosError(error) && (error.response?.status === 403 || error.response?.status === 422)) {
      return;
    }
    toast.error(getFriendlyErrorMessage(error), { duration: 5000 });
  },
});

const saveDisabled = computed(() => saveBlockedByProcessing.value || isCreatePending.value);

const saveDisabledTooltip = computed(() => {
  if (isCreatePending.value) {
    return 'Saving live track actions…';
  }
  if (saveBlockedByProcessing.value) {
    return 'Cannot save while a live track is still Processing match updates';
  }
  return undefined;
});

type ProcessRowsOutcome = { allowLeave: boolean };

function processRowsHandler(): Promise<ProcessRowsOutcome> {
  if (isCreatePending.value) {
    return Promise.resolve({ allowLeave: false });
  }

  if (saveBlockedByProcessing.value) {
    toast.warning('Cannot save while a live track is still Processing match updates', {
      duration: 5000,
    });
    return Promise.resolve({ allowLeave: false });
  }

  const actions = generateActionsPayload();

  if (actions.length === 0) {
    toast.info('No actions to process', { duration: 3000 });
    return Promise.resolve({ allowLeave: false });
  }

  return submitLiveTrackActionsBulk(actions)
    .then((result) => ({ allowLeave: liveTrackBulkSaveAllowsLeave(result) }))
    .catch(() => ({ allowLeave: false }));
}

/** Party IDs for display: from live_track arrays or from match row single IDs. */
function getPartyIdsForDisplay(row: TableRowData, columnId: string): number[] {
  if (columnId === 'match_sender_party_ids') {
    const fromLive = row.match_sender_party_ids;
    if (Array.isArray(fromLive) && fromLive.length) return fromLive.map((id: unknown) => Number(id)).filter((n) => !isNaN(n));
    const single = row.raw_ddex_party_sender_id;
    if (single != null) return [Number(single)];
    return [];
  }
  if (columnId === 'match_sent_on_behalf_of_party_ids') {
    const fromLive = row.match_sent_on_behalf_of_party_ids;
    if (Array.isArray(fromLive) && fromLive.length) return fromLive.map((id: unknown) => Number(id)).filter((n) => !isNaN(n));
    const single = row.raw_ddex_party_sent_on_behalf_of_id;
    if (single != null) return [Number(single)];
    return [];
  }
  return [];
}

// Pager actions go through the unsaved-changes guard before moving the cursor, so pending
// track-match edits aren't silently dropped on a page change.
function navigateHandler(direction: PagerDirection) {
  runWithUnsavedChangesGuard(() => navigateCursor(direction));
}

function applyFilterHandler(parsedQuery: IScope) {
  runWithUnsavedChangesGuard(() => setFilter(parsedQuery));
}

/**
 * Loading a saved view replaces both the filter and the column layout in one
 * shot — both are committed to the store, and the filter change is what could
 * lose pending track-match edits — so it's gated through the unsaved-changes
 * guard just like the previous loadFilter path.
 */
function loadViewHandler(view: SavedViewRecord) {
  // Deliberately the composable's SYNCHRONOUS apply, not a mutation. Applying a
  // view writes live column + filter state and never touches the network, so the
  // guard still wraps a completed mutation — hand it a promise and it can
  // discard pending track-match edits before the guard resolves.
  //
  // The RECORD travels, not its id. This page and its header each mount their
  // own useSavedViews, with their own search boxes and so their own cached
  // pages of results — so looking an id up here searched a list the header's
  // search had never filled, and a view reached that way loaded nothing at all.
  runWithUnsavedChangesGuard(() => savedViewHandlers.onSelectView(view));
}

// Called last, once the guard and everything it reads exist: a view already in
// the cache is applied the moment this runs.
useRoutedSavedView({ applyView: loadViewHandler });

</script>

<template>
  <div class="flex min-h-0 min-w-0 flex-1 flex-col">
    <div
      ref="toolbarRef"
      class="shrink-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/95 z-30"
    >
      <TrackMatchingTableHeader
        :rowsToProcess="queuedLiveTrackCount"
        :save-disabled="saveDisabled"
        :save-disabled-tooltip="saveDisabledTooltip"
        :totalRecords="cachedTotal"
        :current-page="pageNumber"
        :has-next="hasNext"
        :has-prev="hasPrev"
        :loading="isLoading"
        :additionalColumns="additionalColumns"
        @processRows="processRowsHandler"
        @navigate="navigateHandler"
        @applyFilter="applyFilterHandler"
      />
    </div>
    <div class="min-h-0 flex-1 overflow-auto px-4 pb-4 max-md:pb-mobile-area-bar-clearance w-full">
      <DataTable
        table-in-parent-scroll
        :is-table-body-row-hidden="isMatchTableRowHiddenForDataTable"
        :data="processedData"
        :totalRecords="cachedTotal"
        :pageIndex="pageIndex"
        :pageSize="pageSize"
        :columns="unpinnedColumns"
        :pinnedColumns="pinnedColumns"
        :isLoading="isLoading || isCreatePending"
        sticky-header
        row-id-key="id"
        :selected-row-id="openTrackMatchRowId"
        :is-row-openable="isTrackMatchRowOpenable"
        @row-click="onTrackMatchRowClick"
        @columnsResized="columnsResizeHandler"
        @columnsSorted="columnsSortHandler"
        @columnsReordered="columnsReorderHandler"
      >
      <template #cell="{ column, row }">
        <div
          v-if="(row as TableRowData).source === RAW_DDEX_EXPAND_CONTROLS_ROW_SOURCE && column.id !== 'actions'"
          class="min-h-9"
          aria-hidden="true"
        />
        <TrackMatchingColumnActions
          v-else-if="column.id === 'actions'"
          :row="(row as TableRowData)"
          :isLoading="isLoading"
          :is-live-track-processing="isLiveTrackProcessing((row as TableRowData).live_track_id)"
          :expand-raw-ddex-matches-for-live-track="expandRawDdexMatchesForLiveTrack"
          :collapse-raw-ddex-matches-for-live-track="collapseRawDdexMatchesForLiveTrack"
        />
        <div v-else-if="column.id === 'play'" class="px-2 py-1">
          <PlayTrackButton
            v-bind="playButtonPropsForRow(row as TableRowData)"
          />
        </div>
        <TrackMatchingHistoryButton
          v-else-if="column.id === 'history'"
          :row="(row as TableRowData)"
        />
        <div v-else-if="column.id === 'track_match_types'" class="px-2 py-1">
          <!-- Show track_match_types array for Live Track rows -->
          <div v-if="(row as TableRowData).track_match_types?.length" class="flex flex-wrap gap-1">
            <span 
              v-for="type in (row as TableRowData).track_match_types" 
              :key="type"
              class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary"
            >
              {{ type }}
            </span>
          </div>
          <!-- Show single match_type for individual match rows -->
          <div v-else-if="(row as TableRowData).match_type" class="flex flex-wrap gap-1">
            <span 
              class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary"
            >
              {{ (row as TableRowData).match_type }}
            </span>
          </div>
          <span v-else class="text-muted-foreground text-sm">None</span>
        </div>
        <div v-else-if="column.id === 'match_sender_party_ids' || column.id === 'match_sent_on_behalf_of_party_ids'" class="px-2 py-1">
          <div v-if="selectPartyDisplayNamesById(getPartyIdsForDisplay((row as TableRowData), column.id)).length" class="flex flex-wrap gap-1">
            <span
              v-for="item in selectPartyDisplayNamesById(getPartyIdsForDisplay((row as TableRowData), column.id))"
              :key="item.id"
              class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary"
            >
              {{ item.name }}
            </span>
          </div>
          <span v-else class="text-muted-foreground text-sm">—</span>
        </div>
        <TrackMatchingArtistCell
          v-else-if="column.id === 'artist'"
          :row="(row as TableRowData)"
          :field="column.key"
          :dataType="column.dataType"
          :is-live-track-processing="isLiveTrackProcessing((row as TableRowData).live_track_id)"
          @request-rename="onArtistRenameRequest"
        />
        <TrackMatchingColumnMapping
          v-else
          :field="column.key"
          :dataType="column.dataType"
          :data="(row as TableRowData)"
        />
      </template>
    </DataTable>
    </div>

    <TrackMatchingPusherMonitor
      v-for="code in activeMatchingCodes"
      :key="code"
      :live-track-code="code"
      @done="removeMatchingMonitor"
    />

    <ConfirmDialog
      v-model:open="showConfirmModal"
      title="Unsaved Changes"
      message="You have unsaved changes. Are you sure you want to continue? This will discard your changes."
      confirm-text="Save and Continue"
      variant="accent"
      :confirm-disabled="saveDisabled"
      :confirm-disabled-tooltip="saveDisabledTooltip"
      cancel-text="Cancel"
      optional-text="Discard and Continue"
      optional-variant="destructive"
      @optional="discardNavigation"
      @confirm="saveNavigation"
      @cancel="cancelNavigation"
    />

    <ConfirmDialog
      v-model:open="showArtistRenameModal"
      title="Clean artist name"
      :message="artistRenameMessage"
      confirm-text="Queue update"
      variant="accent"
      cancel-text="Cancel"
      @confirm="confirmArtistRename"
      @cancel="cancelArtistRename"
    />
  </div>
</template>
